import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  Modal,
  TextField,
  Typography,
  Alert,
  Button,
} from "@mui/material";
import Link from "@mui/material/Link";
import { LoadingButton } from "@mui/lab";
import * as Yup from "yup";
import { Formik } from "formik";
import FormHelperText from "@mui/material/FormHelperText";
import SnackbarProvider from "app/components/SnackbarProvider";
import { ArrowRightAltOutlined } from "@mui/icons-material";
import FormInstitutionRequest from "./FormInstitutionRequest";
import {
  cancelRequest,
  listInstitution,
} from "app/services/institutionServices/Institution";
import {
  getInstitutionInfo,
  updateInstitution,
} from "app/services/profileServices/Profile";
import { MatxLoading } from "app/components";
import { notify } from "app/utils/notifyUtils";
import { set } from "lodash";

export interface Institution {
  id: number;
  code: string;
  name: string;
  active: number;
  created_by: any;
  approved_by: any;
  created_at: string;
  updated_at: any;
  divisions: Division[];
}

export interface Division {
  id: number;
  name: string;
  institution_id: number;
  created_by: any;
  active: number;
  approved_by: any;
  created_at: string;
  updated_at: any;
}

interface FormValues {
  institution: string;
  unit: string;
  department: string;
}

const validationSchema = Yup.object().shape({
  institution: Yup.string().required("Instituição é um campo obrigatório"),
  unit: Yup.string().required("Cidade é um campo obrigatório"),
  department: Yup.string().required("Unidade é um campo obrigatório"),
});

const FormInstitution = () => {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [institutionLoading, setInstitutionLoading] = useState<boolean>(false);
  const snackbarProvider = SnackbarProvider();

  const [approvationModalOpen, setApprovationModalOpen] = React.useState(false);
  const [institutionReqData, setInstitutionReqData] = useState<FormValues>();
  const handleOpen = () => setApprovationModalOpen(true);
  const handleClose = () => setApprovationModalOpen(false);

  const [institutions, setInstitutions] = useState<any>([]);
  const [currentInstitution, setCurrentInstitution] = useState<any>();
  const [initialValues, setInitialValues] = useState<FormValues>({
    institution: "",
    unit: "",
    department: "",
  });

  const institutionList = async () => {
    setInstitutionLoading(true);
    try {
      const request = await listInstitution();
      setInstitutions(request.data.response);

      const currentData: any = await getInstitutionInfo();
      setCurrentInstitution(currentData.data.response);
      const currentInst = currentData.data.response.userInstitution;
      if (currentInst.department) {
        setInitialValues({
          institution: currentInst.department.division.institution.id || "",
          unit: currentInst.department.division.id || "",
          department: currentInst.department.id || "",
        });
      }
    } catch (e: any) {
      notify("error", "Houve um problema ao carregar a lista de instituições.");
    } finally {
      setInstitutionLoading(false);
    }
  };

  useEffect(() => {
    institutionList();
  }, []); // eslint-disable-line

  const handleFormSubmit = async (values: FormValues) => {
    setFormLoading(true);
    try {
      await updateInstitution(values);
      notify("success", "Dados enviados com sucesso!");
      setTimeout(() => {
        window.location.reload()
      }, 2000);
    } catch (error) {
      notify("error", "Erro ao enviar dados. Tente novamente.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleInstitutionRegister = (values: FormValues) => {
    setInstitutionReqData(values);
    handleOpen();
  };

  const hanfleInstitutionRegisterSuccess = (
    message: string,
    severity:
      | "info"
      | "success"
      | "error"
      | "warning"
      | "errorTable"
      | undefined
  ) => {
    notify(severity, message);
    institutionList();
    handleClose();
  };

  const handleInstitutionRegisterCancel = async () => {
    setFormLoading(true);
    try {
      await cancelRequest();
      notify("success", "Solicitação removida com sucesso!");
    } catch (error) {
      notify("error", "Erro ao enviar dados. Tente novamente.");
    } finally {
      setFormLoading(false);
      institutionList();
    }
  };

  if (institutionLoading) {
    return (
      <div style={{ height: "10vh" }}>
        <MatxLoading />
      </div>
    );
  }

  if (currentInstitution?.institutionRequestPending) {
    return (
      <div style={{ margin: 10 }}>
        <br />
        <Grid container spacing={1}>
          <Grid item sm={12} xs={12}>
            <Alert severity="warning">
              Existe uma solicitação de cadastro de instituição pendente, por
              favor aguarde a aprovação
            </Alert>
          </Grid>

          <Grid item sm={12} xs={12}>
            <Button
              color="error"
              variant="contained"
              onClick={() => handleInstitutionRegisterCancel()}
            >
              Cancelar solicitação de cadastro
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 3,
        width: "100%",
        mx: "auto",
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          marginBottom: 1,
          fontWeight: 600,
          color: "#1a237e",
          letterSpacing: "-0.5px",
        }}
      >
        Cadastro de Instituição
      </Typography>

      <Typography variant="body1" sx={{ marginBottom: 3, color: "#000" }}>
        Preencha os campos com as informações de sua Instituição
      </Typography>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} my={1}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={Boolean(errors.institution && touched.institution)}
                >
                  <Autocomplete
                    noOptionsText="Não foram encontradas Instituições"
                    options={institutions}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    value={
                      institutions.find(
                        (option) => option.id === values.institution
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue("institution", newValue ? newValue.id : "");
                      setFieldValue("unit", ""); // Clear unit when institution changes
                      setFieldValue("department", ""); // Clear department when institution changes
                    }}
                    onBlur={handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Instituição"
                        variant="outlined"
                        name="institution"
                        error={Boolean(
                          errors.institution && touched.institution
                        )}
                      />
                    )}
                  />
                  {touched.institution && errors.institution && (
                    <FormHelperText style={{ color: "red" }}>
                      {errors.institution}
                    </FormHelperText>
                  )}
                </FormControl>
                <Link
                  variant="body2"
                  onClick={() => handleInstitutionRegister(values)}
                  display={values.institution ? "none" : "block"}
                  sx={{
                    color: "#1a237e",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                >
                  Não Encontrou sua Instituição?
                </Link>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              my={1}
              display={values.institution ? "block" : "none"}
            >
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={Boolean(errors.unit && touched.unit)}
                  style={{ marginTop: 16 }}
                >
                  <Autocomplete
                    noOptionsText="Não foram encontradas Cidades para a instituição selecionada"
                    options={
                      institutions.find(
                        (item) => item.id === values.institution
                      )?.divisions || []
                    }
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    value={
                      institutions
                        .find((item) => item.id === values.institution)
                        ?.divisions.find(
                          (division) => division.id === values.unit
                        ) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue("unit", newValue ? newValue.id : "");
                      setFieldValue("department", ""); // Clear department when unit changes
                    }}
                    onBlur={handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cidade"
                        variant="outlined"
                        name="unit"
                        error={Boolean(errors.unit && touched.unit)}
                      />
                    )}
                  />
                  {touched.unit && errors.unit && (
                    <FormHelperText style={{ color: "red" }}>
                      {errors.unit}
                    </FormHelperText>
                  )}
                </FormControl>
                <Link
                  variant="body2"
                  onClick={() => handleInstitutionRegister(values)}
                  display={values.unit ? "none" : "block"}
                >
                  Não Encontrou a Cidade?
                </Link>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              my={1}
              display={values.unit ? "block" : "none"}
            >
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={Boolean(errors.department && touched.department)}
                  style={{ marginTop: 16 }}
                >
                  <Autocomplete
                    noOptionsText="Não foram encontrados Unidades para a Cidade selecionada"
                    options={
                      institutions
                        .find((item) => item.id === values.institution)
                        ?.divisions.find(
                          (division) => division.id === values.unit
                        )?.departments || []
                    }
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    value={
                      institutions
                        .find((item) => item.id === values.institution)
                        ?.divisions.find(
                          (division) => division.id === values.unit
                        )
                        ?.departments.find(
                          (department) => department.id === values.department
                        ) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue("department", newValue ? newValue.id : "");
                    }}
                    onBlur={handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Unidade (Instituto/Departamento/Centro)"
                        variant="outlined"
                        name="department"
                        error={Boolean(errors.department && touched.department)}
                      />
                    )}
                  />
                  {touched.department && errors.department && (
                    <FormHelperText style={{ color: "red" }}>
                      {errors.department}
                    </FormHelperText>
                  )}
                </FormControl>
                <Link
                  variant="body2"
                  onClick={() => handleInstitutionRegister(values)}
                  display={values.department ? "none" : "block"}
                >
                  Não Encontrou sua Unidade?
                </Link>
              </Grid>
            </Grid>

            <Grid container spacing={2} my={2}>
              <Grid item xs={6} sm={6}>
                <LoadingButton
                  type="submit"
                  color="primary"
                  loading={formLoading}
                  variant="contained"
                  endIcon={<ArrowRightAltOutlined fontSize="small" />}
                >
                  Cadastrar
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      <Modal
        open={approvationModalOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <FormInstitutionRequest
            institution={institutionReqData?.institution}
            division={institutionReqData?.unit}
            data={institutions}
            handleClose={hanfleInstitutionRegisterSuccess}
          />
        </div>
      </Modal>
    </Box>
  );
};

export default FormInstitution;
